import { useState, useMemo, useCallback } from 'react';
import { FilterInfo } from '@/app/shared/api/endpoints/SearchInvoices';
import { ButtonFilterSelection } from './../../invoiceUtils/invoiceTypes';
import {
  createDateRangeFilter,
  createSearchFilter,
  createButtonFilters,
  createDueDateGreaterThanFilter,
  createDueDateLessThanFilter,
  combineFilters,
  getDefaultDateFilter,
  createStatusNotEqualFilter,
} from '../../invoiceUtils/invoiceFilters';
import {
  DEFAULT_DATE_FILTER,
  DEFAULT_EXCLUDED_STATUSES,
  FILTER_FIELDS,
} from '@/app/features/DebtorPortal/Dashboard/dashboard.types';

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

const getDefaultDateRange = (): DateRange => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - DEFAULT_DATE_FILTER.filterMonth);
  return { startDate, endDate };
};

export interface UseInvoiceFiltersReturn {
  // Search
  searchTerm: string;
  handleSearchChange: (term: string) => void;

  // Date range
  dateFilter: FilterInfo | undefined;
  selectedDateRangeIndex: number;
  currentDateRange: DateRange | null;
  handleDateRangeChange: (
    index: number,
    startDate: Date,
    endDate: Date,
  ) => void;

  // Due date / widget filters
  dueDateFilter: FilterInfo | undefined;
  isPaymentsNotReceived: boolean;
  handlePaymentsNotReceivedChange: (paymentsNotReceived: boolean) => void;
  handleUpcomingInvoicesChange: () => void;
  handlePaymentsPastDueChange: () => void;

  // Needs Attention
  needsAttention: boolean;
  handleNeedsAttentionChange: (needsAttention: boolean) => void;

  // Button filters
  buttonFilters: ButtonFilterSelection[];
  handleButtonFilterChange: (column: string, value: string) => Promise<void>;
  getActiveFiltersForColumn: (column: string) => string[];

  // Combined filter for API
  combinedFilter: FilterInfo | undefined;

  // Reset all filters
  resetAllFilters: () => void;
  hasActiveFilters: boolean;

  // Reset key for forcing component re-mount
  filterResetKey: number;
}

export const useInvoicesFilters = (
  onFilterChange?: () => void,
): UseInvoiceFiltersReturn => {
  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Date range filter state
  const [dateFilter, setDateFilter] = useState<FilterInfo | undefined>(
    getDefaultDateFilter,
  );
  const [selectedDateRangeIndex, setSelectedDateRangeIndex] = useState(
    DEFAULT_DATE_FILTER.filterIndex,
  );
  const [currentDateRange, setCurrentDateRange] = useState<DateRange | null>(
    getDefaultDateRange,
  );

  // Due date filter state
  const [dueDateFilter, setDueDateFilter] = useState<FilterInfo | undefined>();

  // Payments not received state
  const [isPaymentsNotReceived, setPaymentsNotReceived] = useState(false);

  const [needsAttention, setNeedsAttention] = useState(false);

  // Button filter state
  const [buttonFilters, setButtonFilters] = useState<ButtonFilterSelection[]>(
    [],
  );

  // Reset key - incrementing this forces components to re-mount
  const [filterResetKey, setFilterResetKey] = useState(0);

  // Search handler
  const handleSearchChange = useCallback(
    (term: string) => {
      setSearchTerm(term);
      onFilterChange?.();
    },
    [onFilterChange],
  );

  // Date range handler
  const handleDateRangeChange = useCallback(
    (index: number, startDate: Date, endDate: Date) => {
      setSelectedDateRangeIndex(index);
      setDateFilter(createDateRangeFilter(startDate, endDate));
      setCurrentDateRange({ startDate, endDate });
      onFilterChange?.();
    },
    [onFilterChange],
  );

  // Payments Not Received handler
  const handlePaymentsNotReceivedChange = useCallback(
    (paymentsNotReceived: boolean) => {
      setPaymentsNotReceived(paymentsNotReceived);
      setNeedsAttention(false);
      setDueDateFilter(undefined);
    },
    [],
  );

  // Upcoming invoices handler
  const handleUpcomingInvoicesChange = useCallback(() => {
    const dateNow = new Date();
    setDueDateFilter(createDueDateGreaterThanFilter(dateNow));
    setNeedsAttention(false);
    setPaymentsNotReceived(false);
  }, []);

  // Payments past due handler
  const handlePaymentsPastDueChange = useCallback(() => {
    const dateNow = new Date();
    setDueDateFilter(createDueDateLessThanFilter(dateNow));
    setNeedsAttention(false);
    setPaymentsNotReceived(false);
  }, []);

  const handleNeedsAttentionChange = useCallback((needsAttention: boolean) => {
    setNeedsAttention(needsAttention);
  }, []);

  // Button filter handler
  const handleButtonFilterChange = useCallback(
    async (column: string, value: string): Promise<void> => {
      onFilterChange?.();
      setButtonFilters((currentFilters) => {
        const existingIndex = currentFilters.findIndex(
          (f) => f.column === column && f.value === value,
        );

        if (existingIndex >= 0) {
          return currentFilters.filter((_, index) => index !== existingIndex);
        } else {
          return [...currentFilters, { column, value }];
        }
      });
    },
    [onFilterChange],
  );

  // Get active filters for a specific column
  const getActiveFiltersForColumn = useCallback(
    (column: string): string[] => {
      return buttonFilters
        .filter((f) => f.column === column)
        .map((f) => f.value);
    },
    [buttonFilters],
  );

  // Reset all filters to default state
  const resetAllFilters = useCallback(() => {
    setSearchTerm('');

    const defaultDateRange = getDefaultDateRange();
    setSelectedDateRangeIndex(DEFAULT_DATE_FILTER.filterIndex);
    setDateFilter(getDefaultDateFilter());
    setCurrentDateRange(defaultDateRange);

    setDueDateFilter(undefined);
    setPaymentsNotReceived(false);

    setButtonFilters([]);

    setFilterResetKey((prev) => prev + 1);

    onFilterChange?.();
  }, [onFilterChange]);

  // Check if any filters are active (not in default state)
  const hasActiveFilters = useMemo(() => {
    const hasSearch = searchTerm.trim().length > 0;
    const hasNonDefaultDateRange =
      selectedDateRangeIndex !== DEFAULT_DATE_FILTER.filterIndex;
    const hasDueDateFilter = dueDateFilter !== undefined;
    const hasPaymentsNotReceived = isPaymentsNotReceived;
    const hasButtonFilters = buttonFilters.length > 0;

    return (
      hasSearch ||
      hasNonDefaultDateRange ||
      hasDueDateFilter ||
      hasPaymentsNotReceived ||
      hasButtonFilters
    );
  }, [
    searchTerm,
    selectedDateRangeIndex,
    dueDateFilter,
    isPaymentsNotReceived,
    buttonFilters,
  ]);

  // Get the status values that the user has explicitly selected
  const userSelectedStatuses = useMemo(() => {
    return buttonFilters
      .filter((f) => f.column === FILTER_FIELDS.status)
      .map((f) => f.value);
  }, [buttonFilters]);

  // Build combined filter for API
  const combinedFilter = useMemo((): FilterInfo | undefined => {
    const allFilters: (FilterInfo | undefined)[] = [dateFilter, dueDateFilter];

    // Add search term filter
    if (searchTerm.trim()) {
      allFilters.push(createSearchFilter(searchTerm));
    }

    // Add button filters
    const buttonFilterList = createButtonFilters(buttonFilters);
    allFilters.push(...buttonFilterList);

    // Add default status exclusion filters when user hasn't selected specific statuses
    if (userSelectedStatuses.length === 0) {
      DEFAULT_EXCLUDED_STATUSES.forEach((status) => {
        allFilters.push(createStatusNotEqualFilter(status));
      });
    }

    return combineFilters(allFilters);
  }, [
    dateFilter,
    dueDateFilter,
    searchTerm,
    buttonFilters,
    userSelectedStatuses,
  ]);

  return {
    // Search
    searchTerm,
    handleSearchChange,

    // Date range
    dateFilter,
    selectedDateRangeIndex,
    currentDateRange,
    handleDateRangeChange,

    // Due date / widget filters
    dueDateFilter,
    isPaymentsNotReceived,
    handlePaymentsNotReceivedChange,
    handleUpcomingInvoicesChange,
    handlePaymentsPastDueChange,

    //Needs Attention
    needsAttention,
    handleNeedsAttentionChange,

    // Button filters
    buttonFilters,
    handleButtonFilterChange,
    getActiveFiltersForColumn,

    // Combined filter
    combinedFilter,

    // Reset
    resetAllFilters,
    hasActiveFilters,
    filterResetKey,
  };
};
